/**
 * FIND-08 (Track B) — on-device CONTENT index for the Cmd+K palette. The server `/notes/search` is a
 * network round-trip; this indexes note BODIES locally so a body match is instant and offline-capable,
 * the way Logseq searches its local graph.
 *
 * Coverage caveat (by design, for now): the web client does NOT hold a full local corpus of note
 * bodies — each note's body is a per-note Y.Doc that's only materialized when the note is opened/edited.
 * So this index covers the *working set* (notes opened this session, persisted across reloads), and the
 * palette still falls back to the server for everything not yet indexed. A full-corpus local index
 * (bulk body sync + sqlite-wasm/OPFS, sharing @lapo/core's DDL) is the follow-up — see docs/plans.
 *
 * Reuses the SHARED query grammar (`parseQuery`) so `tag:`/`in:`/phrase/prefix parse identically to the
 * server. Operator/filter queries are deferred to the server (returns []) — this is a fast lexical path
 * for plain free-text over bodies, never a source of results that contradict the grammar.
 */
import { parseQuery, type DocNodeJSON, type ParsedQuery } from '@lapo/core';
import type { ContentHitDTO } from './auth/apiClient.js';

/** One indexed note: its title/type for the row, its block texts for matching + snippets. */
/** One indexed block: its positional id (`b<childIndex>`, matching the server) + flattened text. */
interface LocalBlock {
  blockId: string;
  text: string;
}

interface Entry {
  noteId: string;
  type: string;
  title: string;
  blocks: LocalBlock[];
  updatedAt: number;
}

// v2: blocks are {blockId,text} (was string[]) so a hit can deep-link to its block. Bumping the key
// cleanly discards the old-format cache — it re-indexes as notes are opened.
const STORAGE_KEY = 'lapo-content-index-v2';
/** Bound local storage to the working set (most-recently-updated notes win on eviction). */
const MAX_NOTES = 800;
const SNIPPET_RADIUS = 100;

interface JsonNode {
  type?: string;
  text?: string;
  content?: JsonNode[];
}

/** All text under a node (mirrors noteIndex.collectText). */
function collectText(node: JsonNode): string {
  if (node.type === 'text') return node.text ?? '';
  return (node.content ?? []).map(collectText).join('');
}

/**
 * Top-level blocks of a doc, ids matching the server's chunker (`b<childIndex>`, indexed over ALL
 * top-level children including empties) so a hit's blockId deep-links to the exact block. Empty blocks
 * are skipped for matching but still consume an index, keeping the numbering aligned with the server.
 */
export function blocksFromDoc(doc: DocNodeJSON | null | undefined): LocalBlock[] {
  if (!doc) return [];
  const top = (doc as JsonNode).content ?? [];
  const out: LocalBlock[] = [];
  top.forEach((node, i) => {
    const t = collectText(node).replace(/\s+/g, ' ').trim();
    if (t) out.push({ blockId: `b${i}`, text: t });
  });
  return out;
}

/** Nudge a slice boundary off the middle of a word (mirrors the server snippet builder). */
function wordBoundary(text: string, at: number, dir: 1 | -1): number {
  const alnum = (i: number): boolean => i >= 0 && i < text.length && /[\p{L}\p{N}]/u.test(text[i]!);
  let i = at;
  while (i > 0 && i < text.length && alnum(i - 1) && alnum(i)) i += dir;
  return i;
}

/** Lowercased positive free-text atoms from a parsed query (terms + phrases, no negation/scope). */
function positiveTerms(parsed: ParsedQuery): string[] {
  const out: string[] = [];
  for (const tok of parsed.tokens) {
    if ((tok.kind === 'term' || tok.kind === 'phrase') && !tok.negated && !tok.field) {
      const v = tok.value.trim().toLowerCase();
      if (v) out.push(v);
    }
  }
  return out;
}

/**
 * True when a query uses anything beyond plain positive free text (operators, negation, OR/grouping,
 * field scopes). The local body index only answers plain queries; everything else defers to the server
 * so results can never disagree with the full grammar.
 */
function hasUnsupportedGrammar(parsed: ParsedQuery): boolean {
  const f = parsed.filters;
  const filtered =
    f.tags.length > 0 ||
    f.notTags.length > 0 ||
    f.corpora.length > 0 ||
    f.notCorpora.length > 0 ||
    f.pageTypes.length > 0 ||
    f.notPageTypes.length > 0 ||
    f.links.length > 0 ||
    f.specials.length > 0 ||
    f.starred != null ||
    f.updated != null ||
    f.created != null;
  const structural = parsed.tokens.some(
    (t) => t.kind === 'or' || t.kind === 'group-open' || t.kind === 'group-close' || (t.kind === 'term' && t.negated) || (t.kind === 'phrase' && t.negated) || ((t.kind === 'term' || t.kind === 'phrase') && !!t.field),
  );
  return filtered || structural;
}

/**
 * The block that best represents the match: the first block containing a query term, windowed around
 * the earliest match and trimmed to whole words. Returns the block's id too, so the row can deep-link.
 * Falls back to the first block when nothing matches (shouldn't happen — search only calls this on a hit).
 */
function snippetFor(blocks: LocalBlock[], terms: string[]): { blockId: string; snippet: string } {
  for (const b of blocks) {
    const lower = b.text.toLowerCase();
    let at = -1;
    for (const t of terms) {
      const i = lower.indexOf(t);
      if (i !== -1 && (at === -1 || i < at)) at = i;
    }
    if (at === -1) continue;
    const start = wordBoundary(b.text, Math.max(0, at - SNIPPET_RADIUS), 1);
    const end = wordBoundary(b.text, Math.min(b.text.length, at + SNIPPET_RADIUS), -1);
    return { blockId: b.blockId, snippet: `${start > 0 ? '…' : ''}${b.text.slice(start, end).trim()}${end < b.text.length ? '…' : ''}` };
  }
  const first = blocks[0];
  return { blockId: first?.blockId ?? '', snippet: first?.text.slice(0, SNIPPET_RADIUS * 2).trim() ?? '' };
}

/**
 * In-memory content index over the working set, persisted to localStorage. Query-time matching is a
 * substring AND over positive terms (Logseq-style partial matching), ranked title-first then by term
 * coverage then recency. Instantiate once (module singleton in App) and call `indexNote` from the
 * editor's doc-update hook.
 */
export class LocalContentIndex {
  private entries = new Map<string, Entry>();
  private persistTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(load = true) {
    if (load) this.load();
  }

  /** (Re)index a note from its editor doc. A doc with no text removes the note from the index. */
  indexNote(noteId: string, type: string, title: string, doc: DocNodeJSON | null | undefined): void {
    const blocks = blocksFromDoc(doc);
    if (blocks.length === 0) {
      if (this.entries.delete(noteId)) this.schedulePersist();
      return;
    }
    this.entries.set(noteId, { noteId, type, title, blocks, updatedAt: Date.now() });
    this.evict();
    this.schedulePersist();
  }

  removeNote(noteId: string): void {
    if (this.entries.delete(noteId)) this.schedulePersist();
  }

  /** Is this note covered locally? (Lets the palette know when it must still hit the server.) */
  has(noteId: string): boolean {
    return this.entries.has(noteId);
  }

  /**
   * Instant local content search. Returns ≤ `limit` `ContentHitDTO`s, or [] when the query uses grammar
   * this fast path doesn't cover (the caller then relies on the server). Deterministic ordering.
   */
  search(rawQuery: string, limit = 20): ContentHitDTO[] {
    const q = rawQuery.trim();
    if (!q) return [];
    const parsed = parseQuery(q);
    if (hasUnsupportedGrammar(parsed)) return [];
    const terms = positiveTerms(parsed);
    if (terms.length === 0) return [];

    const scored: Array<{ e: Entry; score: number; blockId: string; snippet: string }> = [];
    for (const e of this.entries.values()) {
      const titleLow = e.title.toLowerCase();
      const hay = `${titleLow} ${e.blocks.map((b) => b.text).join(' ').toLowerCase()}`;
      if (!terms.every((t) => hay.includes(t))) continue; // implicit AND, substring
      const inTitle = terms.some((t) => titleLow.includes(t));
      const coverage = terms.filter((t) => hay.includes(t)).length;
      const score = (inTitle ? 1000 : 0) + coverage;
      scored.push({ e, score, ...snippetFor(e.blocks, terms) });
    }
    scored.sort((a, b) => b.score - a.score || b.e.updatedAt - a.e.updatedAt || a.e.noteId.localeCompare(b.e.noteId));
    return scored.slice(0, limit).map(({ e, blockId, snippet }) => ({ noteId: e.noteId, title: e.title, type: e.type, snippet, blockId }));
  }

  /**
   * LENS-03 — "search this page": every block within ONE note matching the query, best-first. Unlike
   * `search` (≤1 row per note, whole corpus) this returns MULTIPLE block hits from the single open note,
   * so the palette's "Current note" group shows each matching block. Empty when the note isn't locally
   * indexed (never opened this session) or the query uses unsupported grammar.
   */
  searchWithin(noteId: string, rawQuery: string, limit = 8): ContentHitDTO[] {
    const q = rawQuery.trim();
    if (!q) return [];
    const entry = this.entries.get(noteId);
    if (!entry) return [];
    const parsed = parseQuery(q);
    if (hasUnsupportedGrammar(parsed)) return [];
    const terms = positiveTerms(parsed);
    if (terms.length === 0) return [];

    const scored: Array<{ blockId: string; snippet: string; coverage: number }> = [];
    for (const b of entry.blocks) {
      const lower = b.text.toLowerCase();
      const covered = terms.filter((t) => lower.includes(t)).length;
      if (covered === 0) continue;
      const one = snippetFor([b], terms);
      scored.push({ blockId: one.blockId, snippet: one.snippet, coverage: covered });
    }
    scored.sort((a, b) => b.coverage - a.coverage);
    return scored.slice(0, limit).map((s) => ({ noteId, title: entry.title, type: entry.type, snippet: s.snippet, blockId: s.blockId }));
  }

  /** Drop the least-recently-updated entries once over the cap. */
  private evict(): void {
    if (this.entries.size <= MAX_NOTES) return;
    const byRecency = [...this.entries.values()].sort((a, b) => b.updatedAt - a.updatedAt);
    this.entries = new Map(byRecency.slice(0, MAX_NOTES).map((e) => [e.noteId, e]));
  }

  private schedulePersist(): void {
    if (typeof localStorage === 'undefined') return;
    if (this.persistTimer) clearTimeout(this.persistTimer);
    // Coalesce the per-keystroke reindex burst into one write.
    this.persistTimer = setTimeout(() => this.persist(), 500);
  }

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...this.entries.values()]));
    } catch {
      /* quota / private mode — the index still works in-memory this session */
    }
  }

  private load(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const arr = JSON.parse(raw) as Entry[];
      for (const e of arr) if (e?.noteId && Array.isArray(e.blocks)) this.entries.set(e.noteId, e);
    } catch {
      /* corrupt payload — start empty */
    }
  }
}
